import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Cart drawer upsell rail (snippets/cart-drawer-upsell.liquid), also the cart page's "Complete your collection"
 * (snippets/fd-cart-upsell.liquid).
 *
 * Collection mode: Liquid renders the cards inside the drawer, so they update with every cart morph.
 * Recommendations mode: this element loads the section named by data-section (default
 * sections/cart-drawer-upsell.liquid; the cart page's is sections/fd-cart-upsell.liquid) from Shopify's recommendations
 * endpoint for the first cart item. It carries data-skip-subtree-update, so cart morphs keep the loaded cards while
 * still updating its attributes; a change to data-product-id, data-exclude or data-state loads them again.
 *
 * In both modes "+ Add" adds the card's variant through Horizon's flow: POST to the cart add route with the section ids
 * of every cart-items-component, then a CartAddEvent carrying the returned sections, so the drawer, the header bubble
 * and the cart page re-render exactly as they do for the main Add to cart button.
 *
 * @extends {HTMLElement}
 */
class CartDrawerUpsell extends HTMLElement {
  static observedAttributes = ['data-product-id', 'data-exclude', 'data-state'];

  /** @type {AbortController | null} */
  #loading = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    if (this.dataset.mode === 'recommendations') this.#load();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
    this.#loading?.abort();
  }

  /**
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === null || oldValue === newValue || !this.isConnected) return;
    if (this.dataset.mode === 'recommendations') this.#load();
  }

  async #load() {
    const { url, productId, limit, section: sectionId } = this.dataset;
    if (!url || !productId) return;

    this.#loading?.abort();
    const controller = new AbortController();
    this.#loading = controller;
    const params = new URLSearchParams({
      product_id: productId,
      limit: limit || '10',
      section_id: sectionId || 'cart-drawer-upsell',
      intent: 'related',
    });

    try {
      const response = await fetch(`${url}?${params}`, { signal: controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const section = html.querySelector('.shopify-section') ?? html.body;
      // Drop whitespace-only text (the section file leaves a trailing newline): it would stop `.cdz-upsell:empty` from
      // matching, and an empty rail would keep its top margin.
      this.replaceChildren(
        ...[...section.childNodes].filter((node) => node.nodeType !== Node.TEXT_NODE || node.textContent?.trim())
      );
    } catch (error) {
      if (/** @type {Error} */ (error).name !== 'AbortError') this.replaceChildren();
    }
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const select = /** @type {Element} */ (event.target).closest('select[data-cdz-variant]');
    if (!(select instanceof HTMLSelectElement)) return;
    const price = select.selectedOptions[0]?.dataset.price;
    const priceElement = select.closest('[data-cdz-card]')?.querySelector('[data-cdz-price]');
    if (price && priceElement) priceElement.textContent = price;
  };

  /** @param {Event} event */
  #onClick = async (event) => {
    const button = /** @type {Element} */ (event.target).closest('button[data-cdz-add]');
    if (!(button instanceof HTMLButtonElement) || button.getAttribute('aria-busy') === 'true') return;

    const card = button.closest('[data-cdz-card]');
    const variantField = card?.querySelector('[data-cdz-variant]');
    const variantId =
      variantField instanceof HTMLInputElement || variantField instanceof HTMLSelectElement ? variantField.value : '';
    const error = card?.querySelector('[data-cdz-error]');
    if (!variantId) return;

    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    if (error instanceof HTMLElement) error.hidden = true;

    const sectionIds = [
      ...new Set(
        [...document.querySelectorAll('cart-items-component[data-section-id]')].map(
          (element) => /** @type {HTMLElement} */ (element).dataset.sectionId
        )
      ),
    ];

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', {
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
            sections: sectionIds.join(','),
            sections_url: window.location.pathname,
          }),
        })
      );
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || `HTTP ${response.status}`);

      const sectionHtml = sectionIds.map((id) => data.sections?.[id]).find(Boolean);
      const count = sectionHtml
        ? new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector('[ref="cartItemCount"]')?.textContent
        : null;

      document.dispatchEvent(
        new CartAddEvent({}, 'cart-drawer-upsell', {
          source: 'cart-drawer-upsell',
          itemCount: count ? parseInt(count, 10) : undefined,
          variantId: String(variantId),
          productId: button.dataset.productId,
          sections: data.sections,
        })
      );
      // Success: leave the button disabled. The card goes away when the drawer re-renders (collection mode) or the
      // recommendations load again, and an enabled button until then would let a second tap add a second unit.
    } catch {
      button.disabled = false;
      if (error instanceof HTMLElement) {
        error.textContent = this.dataset.errorText || '';
        error.hidden = false;
      }
    } finally {
      button.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('cart-drawer-upsell')) {
  customElements.define('cart-drawer-upsell', CartDrawerUpsell);
}
